import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import { useGameDetailsFetch } from '../../hooks/use-game-details-fetch';
import { GameResultHeader } from '../game-result-header';
import { GameEventCreator } from '../game-event-creator';
import { GameResultEventsList } from '../game-result-events';
import { useGameDetails } from '../../hooks/use-game-details';
import { gameDetailsIsSavingSelector, gameEventsChangedSelector } from 'modules/games/store/selectors';
import UDButton from 'modules/ud-ui/components/button';
import { changeGameDetailsVideoUrl, editGameScore, saveGameDetails } from 'modules/games/store/actions';
import * as styles from './game-result.styles';
import { useTranslation } from 'react-i18next';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { GameDetailsSliceState } from 'modules/games/store/game-details';
import { useModal } from 'modules/ud-ui/components/modal/useModal';
import { GameScoreForm } from '../game-score-form/game-score-form';
import { GameDetailsScore } from 'modules/games/domain/interfaces/GameDetails';
import UDInput from '../../../../ud-form/components/input/component';

const ActionsPanel = styled.div`
  margin-top: 10px;
  padding: 20px 30px;
  display: flex;
  justify-content: end;
`;

const GameVideoUrl = styled.div`
  margin: 16px 0;
`;

const GameVideoUrlSkeleton = styled.div`
  border-radius: 8px;
  height: 38px;
`;

type GameResultProps = {
  gameId: number;
};

export const GameResult = ({ gameId }: GameResultProps) => {
  const { t } = useTranslation()
  const dispatch: ThunkDispatch<GameDetailsSliceState, {}, any> = useDispatch();

  useGameDetailsFetch(gameId);

  const { isLoading, data } = useGameDetails();
  const eventsChanged = useSelector(gameEventsChangedSelector);
  const isDetailsSaving = useSelector(gameDetailsIsSavingSelector);
  const {
    open: openScoreForm,
    close: closeScoreForm,
    Modal: ScoreFormModal,
  } = useModal({ defaultShow: false });

  const isSaveButtonDisabled = useMemo(() => {
    return !eventsChanged || isDetailsSaving;
  }, [eventsChanged, isDetailsSaving]);

  const hasEvents = useMemo(() => (data && data.events.length > 0), [data]);
  const videoUrl = useMemo(() => data?.videoUrls.length ? data.videoUrls[0] : '', [data]);

  const showActionsPanel = useMemo(() => {
    return !isLoading && (hasEvents || eventsChanged);
  }, [isLoading, hasEvents, eventsChanged]);

  const handleChangeScoreClick = useCallback((values: GameDetailsScore) => {
    dispatch(editGameScore(values));
    closeScoreForm();
  }, [closeScoreForm, dispatch]);

  const handleSaveClick = useCallback(() => {
    if (!data || !eventsChanged || isDetailsSaving) {
      return;
    }

    dispatch(saveGameDetails(data))
      .then(() => {
        toast.success(t('games.saveDetailsSuccess'));
      })
      .catch(() => {
        toast.error(t('games.saveDetailsError'));
      });
  }, [data, dispatch, eventsChanged, isDetailsSaving, t]);

  const handleVideoUrlChanged = useCallback((event) => {
    dispatch(changeGameDetailsVideoUrl(event.target.value));
  }, [dispatch]);

  const listClassName = [
    'game-result-events',
    hasEvents ? '' : 'empty'
  ].join(' ');

  return (
    <>
      <styles.GameResult>
        <GameResultHeader
          isLoading={isLoading}
          game={data}
          onEditScoreClick={openScoreForm}
        />

        <GameVideoUrl>
          {isLoading ? (
            <GameVideoUrlSkeleton className="skeleton-block" />
          ) : (
            <UDInput
              name="videoUrl"
              placeholder="Ссылка на трансляцию"
              value={videoUrl}
              onChange={handleVideoUrlChanged}
            />
          )}
        </GameVideoUrl>

        <div className='create-events'>
          <GameEventCreator
            isLoading={isLoading}
            teamIndex={0}
            game={data}
          />
          <GameEventCreator
            isLoading={isLoading}
            teamIndex={1}
            game={data}
          />
        </div>
        <GameResultEventsList
          className={listClassName}
          isLoading={isLoading}
          game={data}
        />
        {showActionsPanel && (
          <ActionsPanel>
            <UDButton
              variant='primary'
              type='button'
              disabled={isSaveButtonDisabled}
              loading={isDetailsSaving}
              onClick={handleSaveClick}
            >{t('games.saveEventsBtnLabel')}</UDButton>
          </ActionsPanel>
        )}
      </styles.GameResult>

      <ScoreFormModal
        header={{title: 'Изменить счет матча', subtitle: ''}}
        containerProps={{style: {width: '500px'}}}
      >
        {data && (
          <GameScoreForm
            teams={data!.teams}
            initialValues={{
              firstTeamScore: data!.result.firstTeamScore,
              secondTeamScore: data!.result.secondTeamScore
            }}
            onSubmit={handleChangeScoreClick}
          />
        )}
      </ScoreFormModal>
    </>
  );
};
